import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import React, { useContext, useState, useMemo } from 'react';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import Torpedo from '../../Torpedo';
import EditorDeviationReactView from '../Views/Thumb';
import DeviationLink from '../../Duperbrowse/DeviationLink';
import Grid from '../../Grid/Grid';
import DaEditorMonoView from '../Views/Mono';
import MediaService from '../../utils/DeviationMediaService';
import { CropFit, GalleryLayout } from '../../DaEditor/plugins/plugin-gallery/types';
import ZoomModal from '../../DeviationViews/Full/Image/ZoomModal';
import { getSrcSet } from '../../DeviationViews/Dumbo/Image/_helpers';
import { getDeviationTitle } from '../../DeviationViews/_helpers';
import { DeviationDuperbrowseContext } from '../../Duperbrowse';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import cleanLinkSrc from '../../utils/xss/cleanLinkSrc';
/**
 * IMPORTANT!
 * Currently to support green/mobile we are using inline
 * styles to render editor related content. Do NOT add
 * classes to anything here for now.
 */ const DEFAULT_MOBILE_WIDTH = 375;
const DEFAULT_DESKTOP_WIDTH = 780;
const DEFAULT_SPACING = 8;
const DeviationWithPossibleLink = (props)=>{
    const { deviation , children  } = props;
    const [showFullview, setShowFullview] = useState(false);
    const fullview = useMemo(()=>MediaService.getLargestFullview(deviation), [
        deviation
    ]);
    if (deviation.isPublished) {
        return /*#__PURE__*/ React.createElement(DeviationLink, {
            deviation: deviation,
            title: getDeviationTitle(deviation)
        }, children);
    }
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        onClick: ()=>{
            setShowFullview(true);
        },
        style: {
            cursor: 'zoom-in'
        }
    }, children), showFullview && /*#__PURE__*/ React.createElement(ZoomModal, {
        src: cleanLinkSrc(fullview.src),
        srcSet: getSrcSet(deviation),
        width: fullview.width,
        height: fullview.height,
        isPixel: false,
        onClose: ()=>{
            setShowFullview(false);
        }
    }));
};
DeviationWithPossibleLink.displayName = 'DeviationWithPossibleLink';
export const Gallery = ({ data =[] , layout =GalleryLayout.GRID , width , settings , isHtml , layoutStyles ={}  })=>{
    const isMobile = useContext(MobileContext);
    const canDuperbrowse = useFlag('yumeruby') && data.every((d)=>d.isPublished);
    // When we only have 1 item and we are not in comments
    // use the single deviation view instead
    if (data.length === 1 && !(settings === null || settings === void 0 ? void 0 : settings.showMediaAlwaysSmall)) {
        const config = {
            alignment: 'right',
            key: 'single',
            showDescription: false,
            showTitle: false,
            size: 'content'
        };
        return /*#__PURE__*/ React.createElement(DaEditorMonoView, {
            config: config,
            settings: settings,
            deviation: data[0],
            width: width,
            isHtml: !!isHtml
        });
    }
    if (canDuperbrowse) {
        return /*#__PURE__*/ React.createElement(DeviationDuperbrowseContext, {
            streamId: data.reduce((prev, current)=>`${prev}-${current.deviationId}`, ''),
            deviations: data
        }, layout === 1 ? renderTorpedo(data, width, isHtml, isMobile, layoutStyles) : renderGrid(data, width, isHtml, isMobile, layoutStyles, settings));
    }
    if (layout === 1) {
        return renderTorpedo(data, width, isHtml, isMobile, layoutStyles);
    }
    return renderGrid(data, width, isHtml, isMobile, layoutStyles, settings);
};
Gallery.displayName = 'EditorGallery';
function renderGrid(deviations, width, isHtml, isMobile, layoutStyles, settings) {
    if (!width || width <= 0) {
        width = isMobile ? DEFAULT_MOBILE_WIDTH : DEFAULT_DESKTOP_WIDTH;
    }
    const getId = (idx)=>deviations[idx].deviationId;
    const spacing = (layoutStyles === null || layoutStyles === void 0 ? void 0 : layoutStyles.spacing) || DEFAULT_SPACING;
    let gridStyles = {};
    let breakpoints = [
        {
            maxWidth: 9999,
            elementWidth: (layoutStyles === null || layoutStyles === void 0 ? void 0 : layoutStyles.gridItemWidth) || 386,
            elementHeight: (layoutStyles === null || layoutStyles === void 0 ? void 0 : layoutStyles.gridItemHeight) || 338
        }
    ];
    // Comments force small media
    if (settings && settings.showMediaAlwaysSmall) {
        breakpoints = [
            {
                maxWidth: 9999,
                elementWidth: 223,
                elementHeight: 146
            }
        ];
        gridStyles = {
            maxWidth: '685px'
        };
    }
    let rowAlign;
    if (layoutStyles.innerAlignment) {
        const settingToCss = {
            left: 'flex-start',
            right: 'flex-end',
            center: 'center'
        };
        gridStyles = {
            display: 'flex',
            justifyContent: settingToCss[layoutStyles.innerAlignment]
        };
        rowAlign = layoutStyles.innerAlignment;
    }
    return /*#__PURE__*/ React.createElement("div", {
        style: gridStyles,
        className: "daeditor-square-grid"
    }, /*#__PURE__*/ React.createElement(Grid, {
        rowAlign: rowAlign,
        containerWidth: width,
        breakpoints: breakpoints,
        elementCount: deviations.length,
        spacing: spacing,
        rowSpacing: spacing,
        getElementId: getId,
        enableScrollOptim: false,
        renderElement: (element)=>{
            /*#__PURE__*/ return React.createElement("div", {
                key: element.index,
                style: _object_spread({}, element.style)
            }, /*#__PURE__*/ React.createElement(DeviationWithPossibleLink, {
                deviation: deviations[element.index]
            }, /*#__PURE__*/ React.createElement(EditorDeviationReactView, {
                isHtml: isHtml,
                deviation: deviations[element.index],
                width: element.width,
                height: element.height,
                allowPreviewUsage: true,
                noCropping: (layoutStyles === null || layoutStyles === void 0 ? void 0 : layoutStyles.cropping) === CropFit.FIT ? true : false
            })));
        }
    }));
}
function renderTorpedo(deviations, width, isHtml, isMobile, layoutStyles) {
    const container = {
        width
    };
    if (!width || width <= 0) {
        width = isMobile ? DEFAULT_MOBILE_WIDTH : DEFAULT_DESKTOP_WIDTH;
    }
    const items = [];
    for (const dev of deviations){
        const largest = MediaService.getThumbDimensions(dev);
        items.push({
            id: dev.deviationId,
            width: largest.width,
            height: largest.height,
            deviation: dev
        });
    }
    let layoutConfig = {
        galleryGutter: true,
        itemSpacingDesktop: 4,
        itemSpacingMobile: 4
    };
    if ((layoutStyles === null || layoutStyles === void 0 ? void 0 : layoutStyles.masonryRowHeight) > 0) {
        layoutConfig = {
            rowSizeDesktop: layoutStyles.masonryRowHeight,
            rowSizeCardDesktop: layoutStyles.masonryRowHeight,
            rowSizeMobile: layoutStyles.masonryRowHeight,
            rowSizeCardMobile: layoutStyles.masonryRowHeight,
            rowSizeMobileLarge: layoutStyles.masonryRowHeight
        };
    }
    if ((layoutStyles === null || layoutStyles === void 0 ? void 0 : layoutStyles.spacing) !== undefined) {
        layoutConfig = _object_spread_props(_object_spread({}, layoutConfig), {
            itemSpacingDesktop: layoutStyles.spacing,
            itemSpacingMobile: layoutStyles.spacing
        });
    }
    let rowAlign;
    let gridStyles = {};
    if (layoutStyles.innerAlignment) {
        const settingToCss = {
            left: 'flex-start',
            right: 'flex-end',
            center: 'center'
        };
        gridStyles = {
            display: 'flex',
            justifyContent: settingToCss[layoutStyles.innerAlignment]
        };
        rowAlign = layoutStyles.innerAlignment;
    }
    // Key is to help with live preview as torpedo is heavily update protected
    return /*#__PURE__*/ React.createElement("div", {
        className: "daeditor-torpedo-grid",
        style: gridStyles
    }, /*#__PURE__*/ React.createElement(Torpedo, {
        rowAlign: rowAlign,
        key: `torpedo-${layoutStyles === null || layoutStyles === void 0 ? void 0 : layoutStyles.cropping}`,
        layoutConfig: layoutConfig,
        items: items,
        container: container,
        enableScrollVisibilityOptimisation: false,
        renderItem: (props, item, itemStyle)=>{
            const deviation = item.dto.deviation;
            const { width: itemWidth , height: itemHeight  } = item;
            return /*#__PURE__*/ React.createElement("div", {
                className: "daeditor-torpedo-grid-item",
                key: `torpedo-${deviation.deviationId}`,
                style: _object_spread({
                    width: itemWidth,
                    height: itemHeight
                }, itemStyle)
            }, /*#__PURE__*/ React.createElement(DeviationWithPossibleLink, {
                deviation: deviation
            }, /*#__PURE__*/ React.createElement(EditorDeviationReactView, {
                isHtml: isHtml,
                deviation: deviation,
                width: itemWidth,
                height: itemHeight,
                allowPreviewUsage: true,
                noCropping: (layoutStyles === null || layoutStyles === void 0 ? void 0 : layoutStyles.cropping) === CropFit.FIT ? true : false
            })));
        }
    }));
}
